import { useEffect, useMemo, useState } from "react";
import { colors, fonts, glyphs } from "../../../../../Styles";
import { PrimaryPopover, NewButton, GenericDeviceTable, ResetTableModal, SecondaryPopover, ResetTableClearQueueModal } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, PopoverPosition, LC_TABLE_IDS } from "../../../../../Definitions";
import { LC_FEED_CURVE, LCFeedCurveArgs, LCFeedCurveResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcFeedCurve";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feed_limit", accessor: "feed_limit", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "pass_feed", accessor: "pass_feed", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "stimuli", accessor: "stimuli", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "milestoneCount", accessor: "milestoneCount", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "componentCount", accessor: "componentCount", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurve_milestones", accessor: "feedcurve_milestones", type: ListData_ValueTypes.Div, sort: false },
	{ headerValue: "feedcurve_components", accessor: "feedcurve_components", type: ListData_ValueTypes.Div, sort: false },
]

export default function FeedCurveDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);
	const [isResetTableClearQueueModalOpen, setIsResetTableClearQueueModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, {data, loading, error }] = useLazyQuery<LCFeedCurveResponse, LCFeedCurveArgs>(LC_FEED_CURVE, { fetchPolicy: "network-only" });

	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({variables: connectionState.deviceConnection});
		}
	}, [connectionState.deviceConnection]);

	const _labelStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2, whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "94px", minHeight: "15px", overflow: "hidden"};
	const _valueStyle = { ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, minHeight: "15px" };

	const tableData = useMemo(() => {
		return data?.controller.lcFeedCurve?.map((e, i) => {
			let milestoneCell: React.ReactNode | undefined = undefined;
			let componentCell: React.ReactNode | undefined = undefined;

			if (e.feedcurve_milestones.length) {
				milestoneCell = (
					<div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
						<SecondaryPopover
							content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
							popoverHeader={"milestones"}
							popoverContent={<>
								<div style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
									<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._labelStyle }}> Milestone ID </div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}> Milestone Index </div>
								</div>
								{e.feedcurve_milestones.map((ele, index) =>
									<div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
										<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._valueStyle }}> {ele.milestoneId} </div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{ele.milestoneIndex}</div>
									</div>
								)}
								</>
							}
						/>
					</div>
				);
			}

			if (e.feedcurve_components.length) {
				componentCell = (
					<div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
						<SecondaryPopover
							content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
							popoverHeader={"components"}
							popoverContent={<>
								<div style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
									<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._labelStyle }}> Component ID </div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}> g/min </div>
								</div>
								{e.feedcurve_components.map((ele, index) =>
									<div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
										<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._valueStyle }}> {ele.componentId} </div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{ele.gram_per_minute}</div>
									</div>
								)}
								</>
							}
						/>
					</div>
				);
			}

			return {
				...e,
				id: e.feedcurve_id,
				feedcurve_milestones: milestoneCell,
				feedcurve_components: componentCell
			}
		}) ?? [];
	}, [data]);

	return (
		<>
			<GenericDeviceTable
				title="Feed Curves"
				columnsData={columnsData}
				data={tableData}
				loading={loading}
				error={error}
				onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
				tabButtons={[
					<NewButton
						key="Reset table"
						content="Reset Table"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>,
					<NewButton
						key="Reset & clear"
						content="Reset & Clear"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableClearQueueModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>
				]}
			/>
			{connectionState.deviceConnection && isResetTableModalOpen &&
				<ResetTableModal
					closeModal={()=>{ setIsResetTableModalOpen(false) }}
					onSuccess={() => fetchData({ variables:connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.feedcurves}
				/>
			}
			{connectionState.deviceConnection && isResetTableClearQueueModalOpen &&
				<ResetTableClearQueueModal
					closeModal={()=>{ setIsResetTableClearQueueModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.feedcurves}
				/>
			}
		</>
	)
}